import {
  OreButton,
  OreHeading,
  OreModal,
  OreModalBox,
  OreModalBoxBody,
  OreModalBoxFooter,
  OreRadioButton,
  OreStack,
  OreText,
} from '@edenredespana/oreneta'
import { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useAccountingStatus, useBankAccount } from '@/Contract/ui/context'
import { contractTranslation } from '@/Contract/ui/contractTranslation'
import { useInvoice } from '@/Invoices/ui/context'
import { currency, iban } from '@/Shared/domain'
import { navigationRoutes } from '@/Shared/ui/routers'
import { formTranslation } from '@/Shared/ui/sharedTranslation'

export type AccountingStatusModalActions = {
  open: (debt: number, origin: string) => void
}

export const AccountingStatusModal = forwardRef<AccountingStatusModalActions>(
  (_, ref): JSX.Element => {
    const { currentIban } = useBankAccount()
    const { processUnpaidInvoices } = useAccountingStatus()
    const { loadInvoices } = useInvoice()

    const [open, setOpen] = useState<boolean>(false)

    const [currentDebt, setCurrentDebt] = useState<number>(0)
    const [currentOrigin, setCurrentOrigin] = useState<string>('')

    const [paymentMethod, setPaymentMethod] = useState<number>(0)

    const { t } = useTranslation()
    const navigate = useNavigate()

    useImperativeHandle(ref, () => ({
      open: async (debt: number, origin: string): Promise<void> => {
        setCurrentDebt(debt)
        setCurrentOrigin(origin)
        setOpen(true)
      },
    }))

    const saveAndClose = async (): Promise<void> => {
      if (paymentMethod === 0) {
        processUnpaidInvoices().then(() => {
          setOpen(false)
          loadInvoices()
        })
      } else {
        navigate(navigationRoutes.changeBankAccount, {
          state: {
            origin: currentOrigin,
          },
        })
      }
    }

    return (
      <OreModal open={open} handleOnClose={() => setOpen(false)}>
        <OreModalBox size="medium" handleOnClose={() => setOpen(false)}>
          <OreModalBoxBody>
            <OreHeading align="left" size="headline-md">
              {t(contractTranslation.accountingStatus.modal.title)}
            </OreHeading>
            <OreStack space="large">
              <OreStack space="small">
                <OreText align="left">
                  {t(contractTranslation.accountingStatus.modal.text1, {
                    debt: currency(currentDebt),
                  })}
                </OreText>
                <OreText align="left">
                  {t(contractTranslation.accountingStatus.modal.text2)}
                </OreText>
              </OreStack>
              <OreStack space="small">
                <OreHeading align="left" size="headline-sm">
                  {t(contractTranslation.accountingStatus.modal.paymentTitle)}
                </OreHeading>
                <OreText align="left">
                  {t(contractTranslation.accountingStatus.modal.paymentText)}
                </OreText>
                <OreStack>
                  <OreRadioButton
                    checked={paymentMethod === 0}
                    onChange={() => setPaymentMethod(0)}
                    label={t(contractTranslation.accountingStatus.modal.paymentOption1, {
                      iban: iban(currentIban),
                    })}
                    data-testid="paymentOptionCurrentIban"
                  />
                  <OreRadioButton
                    checked={paymentMethod === 1}
                    onChange={() => setPaymentMethod(1)}
                    label={t(contractTranslation.accountingStatus.modal.paymentOption2)}
                  />
                </OreStack>
              </OreStack>
            </OreStack>
          </OreModalBoxBody>
          <OreModalBoxFooter>
            <OreButton onClick={() => setOpen(false)} size="small" category="secondary">
              {t(formTranslation.common.buttons.cancel)}
            </OreButton>
            <OreButton
              data-testid="confirm"
              onClick={saveAndClose}
              size="small"
              category="primary"
            >
              {t(formTranslation.common.buttons.confirm)}
            </OreButton>
          </OreModalBoxFooter>
        </OreModalBox>
      </OreModal>
    )
  },
)
